// src/components/Profile/TimedPill.jsx
import React, { useContext, useEffect, useRef } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { TooltipContext } from './TooltipContext';

/**
 * TimedPill - A pill component with optional alignment indicator
 * @param {string} id - Unique identifier
 * @param {string} label - Display text
 * @param {string} tooltipText - Tooltip content
 * @param {string} className - Additional CSS classes
 * @param {string|null} alignmentStatus - 'match' | 'mismatch' | null for alignment indicators
 * @param {string} tooltipClassName - Extra class on the tooltip itself (e.g. 'market-band-tip')
 */
const TimedPill = ({ id, label, tooltipText, className = '', alignmentStatus = null, tooltipClassName = '', onClick = null }) => {
  const { openId, setOpenId } = useContext(TooltipContext);
  const targetRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const show = e => {
    // prevent nested onClick / onTouchStart from flipping states
    e && e.stopPropagation();
    clearTimeout(timerRef.current);
    setOpenId(id);
    timerRef.current = setTimeout(() => {
      setOpenId(curr => curr === id ? null : curr);
    }, 1200);
  };

  // A real activation (click or tap), not the passive onMouseEnter this pill
  // also uses for hover. Additive to the tooltip's own show(), not
  // instead-of: a pill with no tooltipText (e.g. the withheld pay pill)
  // still reveals nothing on its own tooltip -- this just gives a caller a
  // way to react to the SAME activation for something else of its own, like
  // opening a modal, without also firing every time the mouse passes over.
  const activate = e => {
    onClick?.(e);
    show(e);
  };

  const hide = () => {
    clearTimeout(timerRef.current);
    setOpenId(curr => curr === id ? null : curr);
  };

  // Build CSS class list including alignment indicator
  const alignmentClass = alignmentStatus === 'match'
    ? 'alignment-match'
    : alignmentStatus === 'mismatch'
      ? 'alignment-mismatch'
      : '';
  const pillClass = ['pill', className, alignmentClass].filter(Boolean).join(' ');

  return (
    <>
      <span
        ref={targetRef}
        className={pillClass}
        onClick={activate}
        onTouchStart={activate}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {label}
      </span>

      {/* No tooltip content (e.g. a blind listing with no resolved market band)
          means no overlay at all, so the pill reveals nothing on tap. */}
      {tooltipText ? (
        <Overlay
          target={targetRef.current}
          show={openId === id}
          placement="top"
          transition={false}
          rootClose={true}       /* now tapping outside always closes */
          onHide={hide}
        >
          <Tooltip id={`tooltip-${id}`} className={tooltipClassName || undefined} transition="true">
            {tooltipText}
          </Tooltip>
        </Overlay>
      ) : null}
    </>
  );
};

export default TimedPill;

